/** @typedef {import('@/services/call-analytics-service').EnrichedCallLogRow} EnrichedCallLogRow */

/** Normalized ATS statuses that mean profile shared or pipeline advanced (see MyCalls teamAnalytics). */
const PROFILE_SHARED_OR_BEYOND = new Set([
  'profileshared',
  'screenselect',
  'shortlisted',
  'offered',
  'joined',
])

const HYGIENE_CHECK_LABELS = Object.freeze({
  location_discussed: 'Location Discussed',
  position_discussed: 'Position Discussed',
  ctc_range_discussed: 'CTC Range Discussed',
  job_details_provided: 'Job Details Provided',
  requirements_discussed: 'Requirements Discussed',
  notice_period_discussed: 'Notice Period Discussed',
})

/**
 * Lift slim list-metadata aliases (hygiene_score, interest_level, …) into the
 * nested badge shape History / KPIs already read. Does not invent scores.
 * @param {object|null|undefined} analysis
 * @returns {object|null}
 */
export function normalizeAnalysisShape(analysis) {
  if (!analysis || typeof analysis !== 'object') return analysis || null

  const hygieneScore = analysis.hygiene_checks?.overall_hygiene_score
    ?? analysis.hygiene_score
    ?? analysis.overall_hygiene_score
  const interestLevel = analysis.candidate_interest?.level
    ?? analysis.interest_level
    ?? (typeof analysis.interest === 'string' ? analysis.interest : analysis.interest?.level)
  const interested = analysis.candidate_interest?.interested
    ?? analysis.interested
    ?? (analysis.interest && typeof analysis.interest === 'object' ? analysis.interest.interested : undefined)
  const outcomeCategory = analysis.call_outcome?.category
    ?? analysis.outcome_category
    ?? (typeof analysis.outcome === 'string' ? analysis.outcome : analysis.outcome?.category)
  const quality = analysis.call_quality && typeof analysis.call_quality === 'object'
    ? analysis.call_quality
    : null
  const engagement = quality?.candidate_engagement ?? analysis.quality_engagement
  const professionalism = quality?.recruiter_professionalism ?? analysis.quality_professionalism
  const redFlags = quality?.red_flags ?? analysis.red_flags
  const redFlagCount = quality?.red_flag_count ?? analysis.red_flag_count

  const next = { ...analysis }
  if (!next.call_summary) {
    const summaryAlias = analysis.summary || analysis.callSummary || analysis.Summary
    if (summaryAlias) next.call_summary = summaryAlias
  }
  if (!next.hygiene_checks && analysis.hygieneChecks) {
    next.hygiene_checks = analysis.hygieneChecks
  }
  if (hygieneScore != null && (next.hygiene_checks == null || next.hygiene_checks.overall_hygiene_score == null)) {
    next.hygiene_checks = { ...(next.hygiene_checks || {}), overall_hygiene_score: Number(hygieneScore) }
  }
  if ((interestLevel != null || interested != null) && !next.candidate_interest) {
    next.candidate_interest = { level: interestLevel, interested }
  }
  if (outcomeCategory != null && !next.call_outcome) {
    next.call_outcome = { category: outcomeCategory }
  }
  if (!next.call_quality && (engagement || professionalism || redFlags || redFlagCount != null)) {
    next.call_quality = {
      candidate_engagement: engagement,
      recruiter_professionalism: professionalism,
      red_flags: redFlags,
      red_flag_count: redFlagCount,
    }
  }
  return next
}

/**
 * Parse call_analysis from a mapped call row or raw API row.
 * Accepts both slim list metadata and the full click-payload.
 * @param {object|null|undefined} row
 * @returns {object|null}
 */
export function parseAnalysis(row) {
  if (!row) return null
  let analysis = row._callAnalysis != null
    ? row._callAnalysis
    : (row.call_analysis ?? row.callAnalysis ?? null)
  if (analysis == null && row.raw_payload) {
    let raw = row.raw_payload
    if (typeof raw === 'string') {
      try { raw = JSON.parse(raw) } catch { raw = null }
    }
    if (raw && typeof raw === 'object') {
      analysis = raw.call_analysis ?? raw.callAnalysis ?? null
    }
  }
  if (analysis == null) return null
  if (typeof analysis === 'string') {
    try {
      analysis = JSON.parse(analysis)
    } catch {
      return null
    }
  }
  return typeof analysis === 'object' && analysis !== null
    ? normalizeAnalysisShape(analysis)
    : null
}

/** True when analysis includes a usable transcript (full payload, not slim summary). */
export function hasTranscriptText(analysis) {
  if (!analysis || typeof analysis !== 'object') return false
  const t = analysis.transcript
  if (t == null) return false
  if (typeof t === 'string') return t.trim().length > 0
  if (typeof t === 'object') {
    const text = t.text != null ? String(t.text).trim() : ''
    return text.length > 0 || Number(t.word_count) > 0
  }
  return false
}

/** Drop transcript from an analysis object. Leaves scores / flags / call_summary. */
export function stripAnalysisTranscript(analysis) {
  if (!analysis || typeof analysis !== 'object') return analysis
  if (analysis.transcript == null) return analysis
  const next = { ...analysis }
  delete next.transcript
  return next
}

function slimAnalysisField(value) {
  if (value == null) return value
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      const slim = stripAnalysisTranscript(parsed)
      return slim === parsed ? value : slim
    } catch {
      return value
    }
  }
  return stripAnalysisTranscript(value)
}

/** Strip transcripts from a raw call-analytics API row (list fallback). */
export function stripRawCallTranscripts(row) {
  if (!row || typeof row !== 'object') return row
  let changed = false
  const next = { ...row }
  if (next.call_analysis != null) {
    const slim = slimAnalysisField(next.call_analysis)
    if (slim !== next.call_analysis) {
      next.call_analysis = slim
      changed = true
    }
  }
  if (next.callAnalysis != null) {
    const slim = slimAnalysisField(next.callAnalysis)
    if (slim !== next.callAnalysis) {
      next.callAnalysis = slim
      changed = true
    }
  }
  return changed ? next : row
}

/** Strip leftover analysis from a mapped MyCalls row before it enters the live period set. */
export function stripMappedCallTranscripts(row) {
  return slimMappedListRow(row)
}

function parseMaybeAnalysis(value) {
  if (value == null) return null
  if (typeof value === 'object') return value
  if (typeof value !== 'string' || !value.trim()) return null
  try {
    return JSON.parse(value)
  } catch (e) {
    return null
  }
}

function pickDefined(obj, keys) {
  if (!obj || typeof obj !== 'object') return undefined
  for (let i = 0; i < keys.length; i += 1) {
    const value = obj[keys[i]]
    if (value != null && value !== '') return value
  }
  return undefined
}

function slimPipelineNotes(notes) {
  const parsed = parseMaybeJson(notes)
  const list = Array.isArray(parsed)
    ? parsed
    : (Array.isArray(notes) ? notes : (notes && typeof notes === 'object' ? [notes] : []))
  if (!list.length) return null
  const slim = list.map(note => {
    if (!note || typeof note !== 'object') return null
    const next = {}
    if (note.sid) next.sid = note.sid
    if (note.outcome != null) next.outcome = note.outcome
    if (note.note != null) next.note = note.note
    if (note.savedAt != null) next.savedAt = note.savedAt
    if (note.recruiter != null) next.recruiter = note.recruiter
    const reason = note.share_reason || note.shareReason || note.reason
    if (reason) next.share_reason = reason
    return Object.keys(next).length ? next : null
  }).filter(Boolean)
  return slim.length ? slim : null
}

function liftCallsAvailableSkip(row, payload) {
  if (row.calls_available_skip != null) return row.calls_available_skip
  if (row.callsAvailableSkip != null) return row.callsAvailableSkip
  if (payload.calls_available_skip != null) return payload.calls_available_skip
  if (payload.callsAvailableSkip != null) return payload.callsAvailableSkip
  if (row.calls_available === false || payload.calls_available === false) return true
  const gate = payload.inbound_gate || payload.inboundGate
  if (gate && typeof gate === 'object') {
    if ((gate.applied === true || gate.applied === 1 || gate.applied === 'true') && gate.calls_available === false) {
      return true
    }
    const gateStatus = String(gate.status || '').toLowerCase()
    if (gate.reason === 'paused' || gateStatus === 'not_rang' || gateStatus === 'not-rang') {
      return true
    }
  }
  return undefined
}

function collectListAnalysis(row, payload) {
  const nested = parseMaybeAnalysis(row.call_analysis)
    || parseMaybeAnalysis(row.callAnalysis)
    || parseMaybeAnalysis(payload.call_analysis)
    || parseMaybeAnalysis(payload.callAnalysis)
  const flat = {}
  const hygieneScore = row.hygiene_score ?? row.overall_hygiene_score
  if (hygieneScore != null) flat.hygiene_score = hygieneScore
  if (row.interest_level) flat.interest_level = row.interest_level
  if (row.interested != null) flat.interested = row.interested
  if (row.outcome_category) flat.outcome_category = row.outcome_category
  if (row.quality_engagement) flat.quality_engagement = row.quality_engagement
  if (row.red_flag_count != null) flat.red_flag_count = row.red_flag_count
  if (row.red_flags) flat.red_flags = row.red_flags
  if (row.missed_hygiene) flat.missed_hygiene = row.missed_hygiene
  if (row.suitable != null) flat.suitable = row.suitable
  if (row.job_fit != null) flat.job_fit = row.job_fit
  if (row.jd_fit != null) flat.jd_fit = row.jd_fit
  const analyzedAt = row.analyzed_at || row.call_analysis_at || row.callAnalysisAt
  if (analyzedAt) flat.analyzed_at = analyzedAt
  if (!nested && !Object.keys(flat).length) return null
  return slimAnalysisToListMeta({ ...(nested || {}), ...flat })
}

/**
 * Keep only list-row analysis badges. Drops transcript, captured_details,
 * call_summary, language, per-check hygiene objects, and other modal JSON.
 */
export function slimAnalysisToListMeta(analysis) {
  if (!analysis || typeof analysis !== 'object') return analysis
  const normalized = normalizeAnalysisShape(analysis)
  const hygieneScore = getHygieneScore(normalized)
  const interest = normalized.candidate_interest
  const outcome = normalized.call_outcome
  const quality = getCallQuality(normalized)
  const meta = {}

  const analyzedAt = normalized.analyzed_at || null
  if (analyzedAt) meta.analyzed_at = analyzedAt

  if (hygieneScore != null) {
    meta.hygiene_score = hygieneScore
    meta.hygiene_checks = { overall_hygiene_score: hygieneScore }
  }
  if (interest && (interest.level != null || interest.interested != null || typeof interest.suitable === 'boolean')) {
    meta.candidate_interest = { level: interest.level, interested: interest.interested }
    if (typeof interest.suitable === 'boolean') meta.candidate_interest.suitable = interest.suitable
    if (interest.level) meta.interest_level = interest.level
    if (interest.interested != null) meta.interested = interest.interested
  }
  if (outcome && outcome.category) {
    meta.call_outcome = { category: outcome.category }
    meta.outcome_category = outcome.category
  }
  if (typeof normalized.suitable === 'boolean') meta.suitable = normalized.suitable
  if (typeof normalized.job_fit === 'boolean') meta.job_fit = normalized.job_fit
  if (typeof normalized.jd_fit === 'boolean') meta.jd_fit = normalized.jd_fit
  if (looksNotSuitable(normalized.ai_verdict)
    || looksNotSuitable(normalized.verdict)
    || looksNotSuitable(normalized.recommendation)
    || looksNotSuitable(outcome && outcome.recommendation)
    || looksNotSuitable(outcome && outcome.label)) {
    meta.suitable = false
  }

  if (quality) {
    const slimQuality = {}
    if (quality.candidate_engagement) slimQuality.candidate_engagement = quality.candidate_engagement
    if (quality.recruiter_professionalism) slimQuality.recruiter_professionalism = quality.recruiter_professionalism
    const flags = getRedFlags(normalized)
    if (flags.length) slimQuality.red_flags = flags
    const flagCount = Number(quality.red_flag_count ?? normalized.red_flag_count)
    if (Number.isFinite(flagCount) && flagCount > 0) slimQuality.red_flag_count = flagCount
    else if (flags.length) slimQuality.red_flag_count = flags.length
    if (Object.keys(slimQuality).length) meta.call_quality = slimQuality
  }

  const missedKeys = Array.isArray(normalized.missed_hygiene)
    ? normalized.missed_hygiene
    : (Array.isArray(normalized.missed_hygiene_checks) ? normalized.missed_hygiene_checks : null)
  if (missedKeys && missedKeys.length) {
    meta.missed_hygiene = missedKeys
  } else {
    const missed = getMissedHygieneChecks(normalized)
    if (missed.length) meta.missed_hygiene = missed.map(check => check.key)
  }

  if (!Object.keys(meta).length && hasMeaningfulAnalysisPayload(normalized)) {
    meta.analyzed_at = analyzedAt || true
  }

  return meta
}

/**
 * Timestamp keys API 1 / analysis-summary may send. Slim rows and History
 * read the first defined value — new summary payloads are not always created_at.
 */
export const CALL_ROW_DATE_KEYS = Object.freeze([
  'created_at',
  'date_created',
  'createdAt',
  'call_time',
  'start_time',
  'StartTime',
  'dateCreated',
  'call_date',
  'updated_at',
])

/** Extra Exotel / camelCase aliases already used by mapCall / fat list. */
const CALL_ROW_DATE_ALIASES = Object.freeze([
  ...CALL_ROW_DATE_KEYS,
  'DateCreated',
  'DateUpdated',
  'date_updated',
  'dateUpdated',
  'startTime',
  'timestamp',
])

/**
 * Call-time keys only. History DATE & TIME must not lift candidate / job /
 * analysis / duration / phone fields (those produced May–Aug dates on Sept calls).
 */
export const CALL_ROW_PRIMARY_DATE_KEYS = Object.freeze([
  'created_at',
  'date_created',
  'createdAt',
  'dateCreated',
  'DateCreated',
  'call_time',
  'start_time',
  'StartTime',
  'startTime',
  'call_date',
])

/** Analysis / note / identity times — never treat as the call's DATE & TIME. */
const CALL_ROW_DATE_SCAN_SKIP = new Set([
  'call_analysis_at',
  'analyzed_at',
  'callAnalysisAt',
  'savedAt',
  'processing_time_ms',
  'duration',
  'Duration',
  'candidate_contact_no',
  'from_number',
  'to_number',
  'phone',
  'contact_no',
])

const CALL_ROW_DATE_NEST_SKIP = /payload|transcript|analysis|notes|hygiene|quality|captured|context_resolution|webhook|candidate|job|recording/i
const CALL_ROW_DATE_KEY_HINT = /date|time|created|updated|timestamp|^ts$|_at$/i

function isDateLikeKey(key) {
  if (!key || CALL_ROW_DATE_SCAN_SKIP.has(key)) return false
  if (/^duration$/i.test(key)) return false
  return CALL_ROW_DATE_KEY_HINT.test(key)
}

/**
 * True for ISO / Exotel / YYYY-MM-DD / unix epoch values. Rejects duration
 * seconds, badges, and formatted "—" cells.
 * @param {*} value
 * @returns {boolean}
 */
export function looksLikeCallTimestamp(value) {
  if (value == null || value === '') return false
  if (value instanceof Date) return !Number.isNaN(value.getTime())
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return false
    return (value > 1e12 && value < 2e13) || (value > 1e9 && value < 2e10)
  }
  if (typeof value !== 'string') return false
  const s = value.trim()
  if (!s || s === '—' || s.toLowerCase() === 'invalid date') return false
  if (/^\d{10,13}$/.test(s)) return true
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return true
  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(s)) return true
  return false
}

/**
 * Scan own keys (then one nested object level) for a timestamp-looking value.
 * Used when analysis-summary omits created_at / date_created.
 * @param {object|null|undefined} row
 * @param {number} [depth]
 * @returns {{ key: string, value: string|number|Date }|null}
 */
export function scanCallRowTimestamp(row, depth = 0) {
  if (!row || typeof row !== 'object' || Array.isArray(row) || depth > 2) return null

  for (let i = 0; i < CALL_ROW_DATE_ALIASES.length; i += 1) {
    const key = CALL_ROW_DATE_ALIASES[i]
    if (looksLikeCallTimestamp(row[key])) return { key, value: row[key] }
  }

  const keys = Object.keys(row)
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i]
    if (!isDateLikeKey(key) || CALL_ROW_DATE_NEST_SKIP.test(key)) continue
    if (looksLikeCallTimestamp(row[key])) return { key, value: row[key] }
  }

  if (depth >= 2) return null
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i]
    if (CALL_ROW_DATE_SCAN_SKIP.has(key) || CALL_ROW_DATE_NEST_SKIP.test(key)) continue
    const nested = row[key]
    if (!nested || typeof nested !== 'object' || Array.isArray(nested)) continue
    const found = scanCallRowTimestamp(nested, depth + 1)
    if (found) return found
  }
  return null
}

/**
 * Call time from primary keys only (created_at / date_created / Exotel DateCreated).
 * Does not scan nested candidate or job objects.
 * @param {object|null|undefined} row
 * @returns {string|number|Date|undefined}
 */
export function pickExplicitCallRowDate(row) {
  if (!row || typeof row !== 'object') return undefined
  for (let i = 0; i < CALL_ROW_PRIMARY_DATE_KEYS.length; i += 1) {
    const value = row[CALL_ROW_PRIMARY_DATE_KEYS[i]]
    if (looksLikeCallTimestamp(value)) return value
  }
  return undefined
}

/**
 * First usable call timestamp on a raw or slim row.
 * Primary keys first, then known aliases that look like timestamps.
 * Loose nested scan is last-resort only and skips candidate / job / duration.
 * @param {object|null|undefined} row
 * @returns {string|number|undefined}
 */
export function pickCallRowDate(row) {
  const explicit = pickExplicitCallRowDate(row)
  if (explicit != null) return explicit
  const known = pickDefined(row, CALL_ROW_DATE_ALIASES)
  if (known != null && looksLikeCallTimestamp(known)) return known
  const scanned = scanCallRowTimestamp(row)
  return scanned ? scanned.value : undefined
}

/**
 * Parse DB / Exotel / summary timestamps (IST offset, UTC DateCreated, ISO,
 * YYYY-MM-DD, unix epoch). Returns null when the value is not a real date.
 * @param {*} value
 * @returns {Date|null}
 */
export function parseCallRowTimestamp(value) {
  if (value == null || value === '') return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms = value > 1e12 ? value : (value > 1e9 ? value * 1000 : NaN)
    if (!Number.isFinite(ms)) return null
    const fromNum = new Date(ms)
    return Number.isNaN(fromNum.getTime()) ? null : fromNum
  }
  const raw = String(value).trim()
  if (!raw || raw === '—' || raw.toLowerCase() === 'invalid date') return null
  if (/^\d{10,13}$/.test(raw)) {
    const n = Number(raw)
    const ms = raw.length >= 13 ? n : n * 1000
    const fromEpoch = new Date(ms)
    return Number.isNaN(fromEpoch.getTime()) ? null : fromEpoch
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const dateOnly = new Date(`${raw}T00:00:00+05:30`)
    return Number.isNaN(dateOnly.getTime()) ? null : dateOnly
  }
  let s = raw.replace(/^(\d{4}-\d{2}-\d{2})\s(\d{2}:)/, '$1T$2')
  s = s.replace(/(\d)\s+([+-]\d{4})$/, '$1$2')
  s = s.replace(/([+-])(\d{2})(\d{2})$/, '$1$2:$3')
  if (/[Zz]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s)) {
    const withTz = new Date(s)
    return Number.isNaN(withTz.getTime()) ? null : withTz
  }
  const asUtc = new Date(`${s}Z`)
  return Number.isNaN(asUtc.getTime()) ? null : asUtc
}

/** True when any row lacks an explicit call timestamp (created_at / date_created). */
export function pageMissingCallDates(rows) {
  if (!Array.isArray(rows) || !rows.length) return false
  for (let i = 0; i < rows.length; i += 1) {
    if (!pickExplicitCallRowDate(rows[i])) return true
  }
  return false
}

const IST_MONTHS = Object.freeze([
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec',
])

const IST_PARTS_FMT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Kolkata',
  day: 'numeric',
  month: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
})

const IST_YEAR_FMT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Kolkata',
  year: 'numeric',
})

/**
 * History DATE & TIME in Asia/Kolkata. e.g. `{ date: '8 Sept', time: '2:42 pm IST' }`.
 * @param {*|Date|number|string} value
 * @returns {{ date: string, time: string }}
 */
export function formatHistoryDateIst(value) {
  const d = value instanceof Date ? value : parseCallRowTimestamp(value)
  if (!d || Number.isNaN(d.getTime())) return { date: '—', time: '' }
  let map = {}
  try {
    IST_PARTS_FMT.formatToParts(d).forEach(p => { map[p.type] = p.value })
  } catch (e) {
    return { date: '—', time: '' }
  }
  const monthIdx = Number(map.month) - 1
  const month = IST_MONTHS[monthIdx] || map.month
  const day = String(Number(map.day) || map.day)
  const year = map.year
  const hour = map.hour
  const minute = map.minute
  if (!day || !month || !hour || !minute) return { date: '—', time: '' }
  const dayPeriod = String(map.dayPeriod || 'am').replace(/\./g, '').replace(/\s/g, '').toLowerCase()
  let nowYear = ''
  try { nowYear = IST_YEAR_FMT.format(new Date()) } catch (e) { nowYear = '' }
  const date = year && nowYear && year !== nowYear ? `${day} ${month} ${year}` : `${day} ${month}`
  return { date, time: `${hour}:${minute} ${dayPeriod} IST` }
}

/** Milliseconds for newest-first sort. 0 when the call time is unknown. */
export function callRowSortTimestamp(row) {
  if (!row) return 0
  if (typeof row._ts === 'number' && row._ts) return row._ts
  const parsed = parseCallRowTimestamp(
    row._rawDate || pickExplicitCallRowDate(row) || pickCallRowDate(row),
  )
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed.getTime() : 0
}

export function callRowSortId(row) {
  if (!row) return 0
  const n = Number(row.callDbId != null && row.callDbId !== '' ? row.callDbId : row.id)
  return Number.isFinite(n) ? n : 0
}

/** Newest created_at first; higher id wins when times tie or are missing. */
export function sortCallsNewestFirst(rows) {
  if (!Array.isArray(rows)) return []
  if (rows.length < 2) return rows.slice()
  return rows.slice().sort((a, b) => {
    const tb = callRowSortTimestamp(b)
    const ta = callRowSortTimestamp(a)
    if (tb !== ta) return tb - ta
    return callRowSortId(b) - callRowSortId(a)
  })
}

function fatRowIdentity(row) {
  if (!row || typeof row !== 'object') return { sid: '', id: '' }
  const sid = String(row.sid || row.Sid || row.CallSid || row.callSid || row.call_sid || '').trim()
  const id = row.id != null && row.id !== ''
    ? String(row.id)
    : (row.call_id != null && row.call_id !== '' ? String(row.call_id) : '')
  return { sid, id }
}

/**
 * Copy timestamps from a fat GET /call-analytics page onto slim summary rows
 * matched by sid / id. Does not invent dates or change counts.
 * @param {object[]} slimRows
 * @param {object[]} fatRows
 * @returns {object[]}
 */
export function mergeFatCallDates(slimRows, fatRows) {
  if (!Array.isArray(slimRows) || !slimRows.length) return slimRows
  if (!Array.isArray(fatRows) || !fatRows.length) return slimRows
  const bySid = new Map()
  const byId = new Map()
  fatRows.forEach(fat => {
    const ident = fatRowIdentity(fat)
    if (ident.sid) bySid.set(ident.sid, fat)
    if (ident.id) byId.set(ident.id, fat)
  })
  return slimRows.map(row => {
    if (!row || pickExplicitCallRowDate(row)) return row
    const ident = fatRowIdentity(row)
    const fat = (ident.sid && bySid.get(ident.sid)) || (ident.id && byId.get(ident.id)) || null
    if (!fat) return row
    const payload = parseMaybeJson(fat.raw_payload) || {}
    const webhook = payload.webhook_response && typeof payload.webhook_response === 'object'
      ? (payload.webhook_response.data || payload.webhook_response)
      : {}
    const value = pickExplicitCallRowDate(fat)
      || pickExplicitCallRowDate(payload)
      || pickExplicitCallRowDate(webhook)
    if (value == null) return row
    return { ...row, created_at: value }
  })
}

/**
 * Badge / View All count: length of the painted widget list.
 * Ignore envelope 0 when that list is non-empty.
 * @param {Array|null|undefined} list
 * @param {number|null|undefined} envelopeCount
 * @returns {number}
 */
export function preferPaintedListCount(list, envelopeCount) {
  const n = Array.isArray(list) ? list.length : 0
  if (n > 0) return n
  const env = envelopeCount == null || envelopeCount === '' ? NaN : Number(envelopeCount)
  if (Number.isFinite(env) && env > 0) return env
  return n
}

/**
 * Canonical columns stored from API 1 / fat-list fallback.
 * History, Missed, KPIs, FLAGS, Play, notes, and dial-back read only these.
 */
export const LIST_ROW_ALLOWLIST = Object.freeze([
  'id',
  'sid',
  'created_at',
  'date_created',
  'createdAt',
  'call_time',
  'start_time',
  'StartTime',
  'dateCreated',
  'call_date',
  'updated_at',
  'direction',
  'status',
  'duration',
  'from_number',
  'to_number',
  'candidate_id',
  'candidate_name',
  'candidate_contact_no',
  'candidate_email',
  'candidate_status',
  'job_id',
  'position_name',
  'client_name',
  'client_id',
  'recruiter',
  'recruiter_name',
  'job_vertical',
  'job_category',
  'calls_available_skip',
  'pipeline_notes',
  'share_reason',
  'recording_s3_key',
  'recording_url',
  'call_analysis_status',
  'call_analysis_at',
  'call_analysis',
  'total_count',
])

const MAPPED_LIST_ROW_KEYS = Object.freeze([
  'date',
  'candidate',
  'candidateId',
  'candidateIdDisplay',
  'phone',
  'phoneDialable',
  'email',
  'job',
  'jobId',
  'jobCode',
  'clientName',
  'clientId',
  'candidateStatus',
  'durationSec',
  'duration',
  'status',
  'statusVariant',
  'direction',
  '_rawDirection',
  '_rawStatus',
  '_callsAvailableSkip',
  '_rawDate',
  '_ts',
  '_istDay',
  '_clientNameLower',
  '_rawRecruiter',
  '_recruiterName',
  '_jobVertical',
  '_jobCategory',
  '_pipelineNotes',
  'recordingUrl',
  'recordingS3Key',
  'sid',
  'rowKey',
  'callDbId',
  '_callAnalysis',
  '_callAnalysisStatus',
  '_callAnalysisAt',
  'callTypeTag',
  '_hasAnalysis',
])

/**
 * Pick only the list-table columns from a raw API 1 (or fat-list fallback) row.
 * Lifts needed aliases / payload bits, then drops transcript, raw_payload,
 * captured_details, and any unused extras. Single allowlist for stored list data.
 */
export function slimListRow(row) {
  if (!row || typeof row !== 'object') return row
  const payload = parseMaybeJson(row.raw_payload) || {}
  const ctx = payload.context_resolution && typeof payload.context_resolution === 'object'
    ? payload.context_resolution
    : {}
  const webhook = payload.webhook_response && typeof payload.webhook_response === 'object'
    ? (payload.webhook_response.data || payload.webhook_response)
    : {}

  const lifted = {
    id: pickDefined(row, ['id', 'call_id', 'exotel_call_id', 'callDbId']),
    sid: pickDefined(row, ['sid', 'Sid', 'CallSid', 'callSid', 'call_sid'])
      || pickDefined(payload, ['sid', 'Sid', 'CallSid', 'callSid', 'call_sid'])
      || pickDefined(webhook, ['Sid', 'CallSid', 'sid', 'call_sid']),
    created_at: pickExplicitCallRowDate(row)
      || pickExplicitCallRowDate(payload)
      || pickExplicitCallRowDate(webhook)
      || pickCallRowDate(row)
      || pickCallRowDate(payload)
      || pickCallRowDate(webhook),
    date_created: pickDefined(row, ['date_created', 'DateCreated', 'dateCreated'])
      || pickDefined(payload, ['date_created', 'DateCreated', 'dateCreated']),
    createdAt: pickDefined(row, ['createdAt']),
    call_time: pickDefined(row, ['call_time']),
    start_time: pickDefined(row, ['start_time']),
    StartTime: pickDefined(row, ['StartTime']) || pickDefined(webhook, ['StartTime', 'start_time']),
    dateCreated: pickDefined(row, ['dateCreated', 'DateCreated']),
    call_date: pickDefined(row, ['call_date']),
    updated_at: pickDefined(row, ['updated_at', 'updatedAt', 'DateUpdated', 'date_updated']),
    direction: pickDefined(row, ['direction', 'Direction'])
      || pickDefined(payload, ['direction', 'Direction'])
      || pickDefined(webhook, ['Direction', 'direction']),
    status: pickDefined(row, ['status', 'Status']),
    duration: pickDefined(row, ['duration', 'Duration']) ?? payload.answer_duration,
    from_number: pickDefined(row, ['from_number', 'From', 'from']),
    to_number: pickDefined(row, ['to_number', 'To', 'to']),
    candidate_id: pickDefined(row, ['candidate_id', 'candidateId']) || ctx.candidate_id,
    candidate_name: pickDefined(row, ['candidate_name', 'candidateName']) || ctx.candidate_name,
    candidate_contact_no: pickDefined(row, ['candidate_contact_no', 'contact_no', 'phone']),
    candidate_email: pickDefined(row, ['candidate_email', 'email', 'candidateEmail']),
    candidate_status: pickDefined(row, ['candidate_status']) || ctx.candidate_status || ctx.pipeline_status,
    job_id: pickDefined(row, ['job_id', 'jobId']) || ctx.job_id,
    position_name: pickDefined(row, ['position_name', 'job_name', 'jobName'])
      || payload.position_name,
    client_name: pickDefined(row, ['client_name', 'clientName'])
      || ctx.client_name || ctx.client || payload.client_name || payload.client
      || (typeof row.client === 'string' && !/^\d+$/.test(String(row.client).trim()) ? row.client : ''),
    client_id: pickDefined(row, ['client_id', 'clientId', 'clientID'])
      || ctx.client_id || ctx.clientId || payload.client_id || payload.clientId
      || (typeof row.client === 'number' || (typeof row.client === 'string' && /^\d+$/.test(String(row.client).trim()))
        ? row.client
        : ''),
    recruiter: pickDefined(row, ['recruiter', 'Recruiter']),
    recruiter_name: pickDefined(row, ['recruiter_name']),
    job_vertical: pickDefined(row, ['job_vertical', 'jobVertical', 'JobVertical'])
      || ctx.job_vertical || ctx.vertical || payload.job_vertical,
    job_category: pickDefined(row, ['job_category', 'jobCategory', 'JobCategory'])
      || ctx.job_category || ctx.category || payload.job_category,
    calls_available_skip: liftCallsAvailableSkip(row, payload),
    pipeline_notes: slimPipelineNotes(row.pipeline_notes),
    share_reason: pickDefined(row, ['share_reason', 'shareReason', 'profile_share_reason']),
    recording_s3_key: pickDefined(row, ['recording_s3_key', 'recordingS3Key', 'RecordingS3Key'])
      || webhook.recording_key,
    recording_url: pickDefined(row, [
      'recording_url',
      'recordingUrl',
      'RecordingUrl',
      'recording_s3_url',
      'recordingS3Url',
      'recording_presigned_url',
      'recordingPresignedUrl',
    ]) || webhook.recording || webhook.RecordingUrl || webhook.recording_url,
    call_analysis_status: pickDefined(row, ['call_analysis_status', 'callAnalysisStatus']),
    call_analysis_at: pickDefined(row, ['call_analysis_at', 'callAnalysisAt', 'analyzed_at']),
    call_analysis: collectListAnalysis(row, payload),
    total_count: pickDefined(row, ['total_count', 'totalRecords']),
  }

  const next = {}
  LIST_ROW_ALLOWLIST.forEach(key => {
    const value = lifted[key]
    if (value !== undefined && value !== null && value !== '') {
      next[key] = value
    } else if (value === false || value === 0) {
      next[key] = value
    }
  })
  // Copy only primary call-time keys when summary uses an alias.
  const keepFrom = [row, payload, webhook]
  for (let s = 0; s < keepFrom.length; s += 1) {
    const src = keepFrom[s]
    if (!src || typeof src !== 'object') continue
    for (let i = 0; i < CALL_ROW_PRIMARY_DATE_KEYS.length; i += 1) {
      const key = CALL_ROW_PRIMARY_DATE_KEYS[i]
      if (next[key] != null && next[key] !== '') continue
      if (!looksLikeCallTimestamp(src[key])) continue
      next[key] = src[key]
      if (next.created_at == null) next.created_at = src[key]
    }
  }
  return next
}

/** @deprecated Use slimListRow — same allowlist. */
export function slimRawCallToListMeta(row) {
  return slimListRow(row)
}

/**
 * Fields CallAnalysisDetailModal reads from API 2 (`GET …/:sid/analysis`).
 * Identity (sid/id/name) is optional — the modal title uses the list row.
 * Nested objects are slimed further in slimAnalysisDetail.
 */
export const ANALYSIS_DETAIL_ALLOWLIST = Object.freeze([
  'sid',
  'id',
  'candidate_name',
  'call_summary',
  'candidate_interest',
  'call_outcome',
  'hygiene_checks',
  'hygiene_score',
  'overall_hygiene_score',
  'missed_hygiene',
  'missed_hygiene_checks',
  'call_quality',
  'red_flags',
  'captured_details',
  'analyzed_at',
  'language_detected',
  'is_follow_up_call',
  'is_follow_up',
  'processing_time_ms',
  'meta',
])

const CAPTURED_DETAIL_KEYS = Object.freeze([
  'current_location',
  'expected_ctc',
  'current_ctc',
  'notice_period',
  'skills_mentioned',
  'years_experience',
  'preferred_location',
  'current_company',
  'current_designation',
])

function slimDetailInterest(interest) {
  if (!interest || typeof interest !== 'object') return undefined
  const next = {}
  if (interest.level != null && interest.level !== '') next.level = interest.level
  if (interest.interested != null) next.interested = interest.interested
  if (interest.confidence != null) next.confidence = interest.confidence
  return Object.keys(next).length ? next : undefined
}

function slimDetailOutcome(outcome) {
  if (!outcome || typeof outcome !== 'object') return undefined
  const next = {}
  if (outcome.category != null && outcome.category !== '') next.category = outcome.category
  if (outcome.evidence != null && outcome.evidence !== '') next.evidence = outcome.evidence
  if (outcome.confidence != null) next.confidence = outcome.confidence
  return Object.keys(next).length ? next : undefined
}

function slimDetailHygieneCheck(check) {
  if (check == null) return undefined
  if (typeof check !== 'object') return check
  const next = {}
  if (typeof check.done === 'boolean') next.done = check.done
  if (check.confidence != null) next.confidence = check.confidence
  if (check.notes != null && check.notes !== '') next.notes = check.notes
  if (check.evidence != null && check.evidence !== '') next.evidence = check.evidence
  return Object.keys(next).length ? next : undefined
}

function slimDetailHygieneChecks(checks, hygieneScore) {
  const next = {}
  if (checks && typeof checks === 'object') {
    if (checks.overall_hygiene_score != null) {
      next.overall_hygiene_score = Number(checks.overall_hygiene_score)
    }
    Object.keys(HYGIENE_CHECK_LABELS).forEach(key => {
      if (checks[key] == null) return
      const slim = slimDetailHygieneCheck(checks[key])
      if (slim !== undefined) next[key] = slim
    })
  }
  if (next.overall_hygiene_score == null && hygieneScore != null) {
    next.overall_hygiene_score = Number(hygieneScore)
  }
  return Object.keys(next).length ? next : undefined
}

function slimDetailQuality(quality, redFlags) {
  const next = {}
  if (quality && typeof quality === 'object') {
    if (quality.candidate_engagement) next.candidate_engagement = quality.candidate_engagement
    if (quality.recruiter_professionalism) next.recruiter_professionalism = quality.recruiter_professionalism
  }
  const flags = Array.isArray(redFlags) && redFlags.length
    ? redFlags
    : (quality && Array.isArray(quality.red_flags) ? quality.red_flags : null)
  if (flags && flags.length) next.red_flags = flags
  return Object.keys(next).length ? next : undefined
}

function slimDetailCaptured(details) {
  if (!details || typeof details !== 'object') return undefined
  const next = {}
  CAPTURED_DETAIL_KEYS.forEach(key => {
    const value = details[key]
    if (value != null && value !== '') next[key] = value
  })
  return Object.keys(next).length ? next : undefined
}

function slimDetailMeta(meta, processingTimeMs) {
  const ms = processingTimeMs != null
    ? processingTimeMs
    : (meta && typeof meta === 'object' ? meta.processing_time_ms : null)
  if (ms == null) return undefined
  return { processing_time_ms: ms }
}

/**
 * Keep only the View Analysis modal fields from an API 2 payload.
 * Drops transcript, utterances, raw_payload, recording blobs, and unused
 * nested analysis keys. Does not invent scores.
 * @param {object|string|null|undefined} analysis
 * @returns {object|null}
 */
export function slimAnalysisDetail(analysis) {
  if (analysis == null) return null
  const parsed = typeof analysis === 'string' ? parseMaybeJson(analysis) : analysis
  if (!parsed || typeof parsed !== 'object') return null

  const normalized = normalizeAnalysisShape(parsed)
  const quality = getCallQuality(normalized)
  const hygieneScore = getHygieneScore(normalized)
  const flags = getRedFlags(normalized)

  const lifted = {
    sid: pickDefined(normalized, ['sid', 'Sid', 'CallSid', 'call_sid']),
    id: pickDefined(normalized, ['id', 'call_id', 'callDbId']),
    candidate_name: pickDefined(normalized, ['candidate_name', 'candidateName']),
    call_summary: normalized.call_summary || normalized.summary || normalized.callSummary || undefined,
    candidate_interest: slimDetailInterest(normalized.candidate_interest),
    call_outcome: slimDetailOutcome(normalized.call_outcome),
    hygiene_checks: slimDetailHygieneChecks(
      normalized.hygiene_checks || normalized.hygieneChecks,
      hygieneScore,
    ),
    hygiene_score: hygieneScore != null ? hygieneScore : undefined,
    overall_hygiene_score: hygieneScore != null ? hygieneScore : undefined,
    missed_hygiene: Array.isArray(normalized.missed_hygiene) && normalized.missed_hygiene.length
      ? normalized.missed_hygiene
      : undefined,
    missed_hygiene_checks: Array.isArray(normalized.missed_hygiene_checks)
      && normalized.missed_hygiene_checks.length
      ? normalized.missed_hygiene_checks
      : undefined,
    call_quality: slimDetailQuality(quality, flags),
    red_flags: flags.length ? flags : undefined,
    captured_details: slimDetailCaptured(normalized.captured_details),
    analyzed_at: pickDefined(normalized, ['analyzed_at', 'call_analysis_at', 'callAnalysisAt']),
    language_detected: normalized.language_detected || undefined,
    is_follow_up_call: normalized.is_follow_up_call != null
      ? normalized.is_follow_up_call
      : undefined,
    is_follow_up: normalized.is_follow_up != null ? normalized.is_follow_up : undefined,
    processing_time_ms: normalized.processing_time_ms != null
      ? normalized.processing_time_ms
      : undefined,
    meta: slimDetailMeta(normalized.meta, normalized.processing_time_ms),
  }

  const next = {}
  ANALYSIS_DETAIL_ALLOWLIST.forEach(key => {
    const value = lifted[key]
    if (value !== undefined && value !== null && value !== '') {
      next[key] = value
    } else if (value === false || value === 0) {
      next[key] = value
    }
  })
  return Object.keys(next).length ? next : null
}

/** Pick only mapped History / Missed / KPI columns. Slims leftover analysis. */
export function slimMappedListRow(row) {
  if (!row || typeof row !== 'object') return row
  const next = {}
  MAPPED_LIST_ROW_KEYS.forEach(key => {
    if (row[key] !== undefined) next[key] = row[key]
  })
  if (next._callAnalysis) {
    next._callAnalysis = slimAnalysisToListMeta(next._callAnalysis)
  }
  return Object.freeze(next)
}

const COMPLETED_ANALYSIS_STATUSES = new Set([
  'completed',
  'complete',
  'done',
  'success',
  'analyzed',
])

/**
 * Whether a parsed analysis object has usable AI metrics (not just an empty shell).
 * @param {object|null|undefined} analysis
 * @returns {boolean}
 */
function hasAnalysisBadgeMeta(row, analysis) {
  if (row && (row._callAnalysisAt || row.call_analysis_at || row.callAnalysisAt)) return true
  if (!analysis || typeof analysis !== 'object') return false
  return !!(
    analysis.analyzed_at
    || analysis.call_summary
    || analysis.hygiene_score != null
    || analysis.overall_hygiene_score != null
    || analysis.interest_level
    || analysis.outcome_category
    || analysis.hygiene_checks
    || analysis.call_quality
    || analysis.candidate_interest
    || analysis.call_outcome
    || analysis.red_flags
    || analysis.red_flag_count != null
  )
}

function hasMeaningfulAnalysisPayload(analysis) {
  if (!analysis || typeof analysis !== 'object') return false
  return !!(
    analysis.hygiene_checks
    || analysis.call_quality
    || analysis.candidate_interest
    || analysis.call_outcome
    || analysis.hygiene_score != null
    || analysis.interest_level
    || analysis.outcome_category
    || analysis.call_summary
    || analysis.transcript
  )
}

/**
 * True when a call has completed AI analysis worth showing in Call History.
 * Completed status is enough (API 1 may send badges / analyzed_at only).
 * If status was omitted, accept slim badge metadata or a full payload.
 * @param {object|null|undefined} row
 * @returns {boolean}
 */
export function hasCompletedAnalysis(row) {
  if (!row) return false
  const analysis = parseAnalysis(row)
  const status = String(
    row._callAnalysisStatus
      ?? row.call_analysis_status
      ?? row.callAnalysisStatus
      ?? '',
  ).trim().toLowerCase()

  if (status) {
    return COMPLETED_ANALYSIS_STATUSES.has(status)
  }

  return hasMeaningfulAnalysisPayload(analysis) || hasAnalysisBadgeMeta(row, analysis)
}

/**
 * Normalize candidate/ATS status for comparison (case-insensitive, no spaces).
 * @param {string|null|undefined} status
 * @returns {string}
 */
export function normalizeCandidateStatus(status) {
  return String(status || '').toLowerCase().replace(/\s/g, '')
}

/**
 * @param {string|null|undefined} status
 * @returns {boolean}
 */
export function isProfileSharedOrBeyond(status) {
  const normalized = normalizeCandidateStatus(status)
  return normalized.length > 0 && PROFILE_SHARED_OR_BEYOND.has(normalized)
}

function firstNonEmptyString(...values) {
  for (let i = 0; i < values.length; i += 1) {
    const value = values[i]
    if (value == null || value === false) continue
    const text = String(value).trim()
    if (text && text !== '—') return text
  }
  return ''
}

function parseMaybeJson(value) {
  if (value == null) return null
  if (typeof value === 'object') return value
  if (typeof value !== 'string' || !value.trim()) return null
  try {
    return JSON.parse(value)
  } catch (e) {
    return null
  }
}

function looksLikeAnalysisObject(item) {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return false
  return !!(
    item.hygiene_checks
    || item.call_quality
    || item.candidate_interest
    || item.call_outcome
    || item.call_summary
    || item.captured_details
    || item.analyzed_at
    || item.language_detected
    || item.transcript
  )
}

/**
 * Pull call_analysis from a detail-endpoint body. Accepts stringified JSON
 * and `data.call_analysis` (live 09-08 envelope).
 * @param {object|null|undefined} body
 * @returns {object|null}
 */
export function extractCallAnalysisPayload(body) {
  if (!body || typeof body !== 'object') return null
  const candidates = [
    body.data && body.data.call_analysis,
    body.data && body.data.analysis,
    body.call_analysis,
    body.analysis,
    body.data,
  ]
  for (let i = 0; i < candidates.length; i += 1) {
    const item = parseMaybeJson(candidates[i]) || candidates[i]
    if (looksLikeAnalysisObject(item)) return item
    if (item && typeof item === 'object' && !Array.isArray(item)) {
      const nested = parseMaybeJson(item.call_analysis) || parseMaybeJson(item.analysis)
      if (looksLikeAnalysisObject(nested)) return nested
    }
  }
  return null
}

/**
 * Same merge the View Analysis modal applies after API 2: extract + slim.
 * @param {object|null|undefined} body
 * @returns {object|null}
 */
export function applyCallAnalysisDetailBody(body) {
  const extracted = extractCallAnalysisPayload(body)
  return extracted ? slimAnalysisDetail(extracted) : null
}

/**
 * @param {object|null|undefined} row
 * @returns {string}
 */
export function getCandidateStatusFromRow(row) {
  if (!row) return ''
  const raw = row.raw && typeof row.raw === 'object' ? row.raw : row
  const payload = parseMaybeJson(raw.raw_payload || row.raw_payload) || {}
  const ctx = payload.context_resolution && typeof payload.context_resolution === 'object'
    ? payload.context_resolution
    : {}
  const status = firstNonEmptyString(
    row.candidateStatus,
    row.candidate_status,
    raw.candidate_status,
    ctx.candidate_status,
    ctx.pipeline_status,
    row.pipeline_status,
  )
  return status
}

function looksNotSuitable(value) {
  const text = String(value || '').toLowerCase().replace(/[_-]+/g, ' ').trim()
  if (!text) return false
  return text.includes('not suitable')
    || text.includes('unsuitable')
    || text === 'not relevant'
    || text === 'not_suitable'
}

/**
 * Whether AI analysis indicates the candidate was not interested or had low interest.
 * Conservative: explicit false, not_interested outcome, or low level without interested=true.
 * @param {object|null|undefined} analysis
 * @returns {boolean}
 */
export function isNotInterestedByAnalysis(analysis) {
  if (!analysis) return false
  const interest = analysis.candidate_interest
  const category = String(
    analysis.call_outcome?.category
    || analysis.outcome_category
    || '',
  ).toLowerCase()
  if (analysis.interested === false) return true
  if (interest?.interested === false) return true
  if (category === 'not_interested') return true
  const level = String(interest?.level || analysis.interest_level || '').toLowerCase()
  if (level === 'low' && interest?.interested !== true && analysis.interested !== true) return true
  return false
}

/**
 * Whether AI analysis indicates the candidate is not suitable for the role.
 * @param {object|null|undefined} analysis
 * @returns {boolean}
 */
export function isNotSuitableByAnalysis(analysis) {
  if (!analysis) return false
  if (analysis.suitable === false || analysis.candidate_interest?.suitable === false) return true
  if (analysis.job_fit === false || analysis.jd_fit === false) return true
  const category = String(analysis.call_outcome?.category || '').toLowerCase()
  if (category === 'not_suitable' || category === 'unsuitable' || category === 'not_relevant') return true
  const verdictFields = [
    analysis.ai_verdict,
    analysis.verdict,
    analysis.recommendation,
    analysis.suitability,
    analysis.call_outcome?.recommendation,
    analysis.call_outcome?.label,
    analysis.candidate_interest?.suitability,
    analysis.job_fit && analysis.job_fit.level,
    analysis.suitability && analysis.suitability.level,
  ]
  return verdictFields.some(looksNotSuitable)
}

/**
 * @param {object|null|undefined} analysis
 * @returns {'Not interested'|'Not suitable'|'Not interested / Not suitable'|null}
 */
export function getAiNegativeVerdictLabel(analysis) {
  const notInterested = isNotInterestedByAnalysis(analysis)
  const notSuitable = isNotSuitableByAnalysis(analysis)
  if (notInterested && notSuitable) return 'Not interested / Not suitable'
  if (notSuitable) return 'Not suitable'
  if (notInterested) return 'Not interested'
  return null
}

/**
 * Flag when completed AI analysis shows not interested / not suitable
 * but recruiter moved the candidate to profile-shared+.
 * @param {object|null|undefined} row
 * @returns {boolean}
 */
export function hasInterestStatusMismatch(row) {
  if (!hasCompletedAnalysis(row)) return false
  const analysis = parseAnalysis(row)
  if (!isNotInterestedByAnalysis(analysis) && !isNotSuitableByAnalysis(analysis)) return false
  return isProfileSharedOrBeyond(getCandidateStatusFromRow(row))
}

/**
 * Recruiter/ATS note for why a profile was shared, if the API sent one.
 * @param {object|null|undefined} row
 * @returns {string}
 */
export function getShareReasonFromRow(row) {
  if (!row) return ''
  const raw = row.raw && typeof row.raw === 'object' ? row.raw : row
  let notes = row._pipelineNotes || row.pipeline_notes || raw.pipeline_notes
  notes = parseMaybeJson(notes) || notes
  let noteReason = ''
  const noteList = Array.isArray(notes) ? notes : (notes && typeof notes === 'object' ? [notes] : [])
  for (let i = 0; i < noteList.length; i += 1) {
    const note = noteList[i]
    if (!note || typeof note !== 'object') continue
    noteReason = firstNonEmptyString(
      note.share_reason,
      note.shareReason,
      note.reason,
      note.note,
      note.comment,
    )
    if (noteReason) break
  }
  return firstNonEmptyString(
    row.share_reason,
    row.shareReason,
    row.profile_share_reason,
    raw.share_reason,
    raw.shareReason,
    raw.reason,
    row.reason,
    row.comment,
    noteReason,
  )
}

/**
 * Why the profile was shared despite a negative AI verdict.
 * @param {object|null|undefined} row
 * @returns {string}
 */
export function getWhyProfileShared(row) {
  const existing = getShareReasonFromRow(row)
  if (existing) return existing
  const status = getCandidateStatusFromRow(row)
  const fallback = 'Profile was shared after AI said not interested/not suitable'
  return status ? `${status} — ${fallback}` : fallback
}

/**
 * Human-readable explanation for interest/status mismatch tooltips.
 * @param {object|null|undefined} row
 * @returns {string|null}
 */
export function getInterestMismatchReason(row) {
  if (!hasInterestStatusMismatch(row)) return null
  const analysis = parseAnalysis(row)
  const status = getCandidateStatusFromRow(row) || 'Profile Shared or beyond'
  const verdict = getAiNegativeVerdictLabel(analysis)
  const parts = []
  if (verdict) {
    parts.push(`AI analysis: ${verdict.toLowerCase()}`)
  } else if (analysis?.candidate_interest?.interested === false) {
    parts.push('AI analysis: candidate not interested')
  } else if (String(analysis?.call_outcome?.category || '').toLowerCase() === 'not_interested') {
    parts.push('AI call outcome: not interested')
  } else if (String(analysis?.candidate_interest?.level || '').toLowerCase() === 'low') {
    parts.push('AI analysis: low interest')
  }
  parts.push(`but candidate status is "${status}"`)
  return parts.join(', ')
}

/**
 * @param {string|null|undefined} s
 * @returns {boolean}
 */
export function isConnectedStatus(s) {
  const lower = String(s || '').toLowerCase()
  return lower === 'answered' || lower === 'completed'
}

/**
 * Whether a mapped or raw call row represents an answered/connected call.
 * Matches MyCalls KPI "connected" logic (Exotel answered/completed).
 * @param {object|null|undefined} row
 * @returns {boolean}
 */
export function isAnsweredCall(row) {
  if (!row) return false
  return isConnectedStatus(row._rawStatus) || isConnectedStatus(row.status)
}

/**
 * Completed analysis on a mapped or raw History row.
 * Prefers the mapped `_hasAnalysis` flag when present.
 * @param {object|null|undefined} row
 * @returns {boolean}
 */
export function callHasCompletedAnalysis(row) {
  if (!row) return false
  if (row._hasAnalysis != null) return !!row._hasAnalysis
  return hasCompletedAnalysis(row)
}

/**
 * History **Not analyzed**: Answered / completed only, and no completed analysis.
 * Excludes queued, not-answered, missed, failed, in-progress, busy.
 * @param {object|null|undefined} row
 * @returns {boolean}
 */
export function isUnanalyzedAnsweredCall(row) {
  if (!row) return false
  if (!isAnsweredCall(row)) return false
  return !callHasCompletedAnalysis(row)
}

/**
 * Apply History toolbar All / Analyzed only / Not analyzed.
 * History-only — do not use for KPI widgets or live period drains.
 * @param {string} callAnalysis
 * @param {Array<object>} rows
 * @returns {Array<object>}
 */
export function applyHistoryAnalysisFilter(callAnalysis, rows) {
  const list = Array.isArray(rows) ? rows : []
  if (callAnalysis === 'analyzed') {
    return list.filter(c => callHasCompletedAnalysis(c))
  }
  if (callAnalysis === 'not_analyzed') {
    return list.filter(c => isUnanalyzedAnsweredCall(c))
  }
  return list
}

/**
 * Whether the View Analysis action should appear for a call history row.
 * @param {object|null|undefined} row
 * @returns {boolean}
 */
export function canViewCallAnalysis(row) {
  if (!row || !hasCompletedAnalysis(row)) return false
  return isConnectedStatus(row._rawStatus) || row.status === 'Answered'
}

/**
 * @param {number|null|undefined} score
 * @returns {'danger'|'warning'|'success'|'secondary'}
 */
export function hygieneScoreVariant(score) {
  if (score == null || Number.isNaN(Number(score))) return 'light-secondary'
  const n = Number(score)
  if (n < 0.4) return 'light-secondary'
  if (n >= 0.8) return 'light-primary'
  return 'light-secondary'
}

/**
 * @param {number|null|undefined} score
 * @returns {string}
 */
export function hygieneScoreColor(score) {
  if (score == null || Number.isNaN(Number(score))) return '#b9b9c3'
  const n = Number(score)
  if (n < 0.4) return '#b9b9c3'
  return '#20c997'
}

/**
 * @param {string|null|undefined} level
 * @returns {'success'|'warning'|'danger'|'secondary'|'info'}
 */
export function interestBadgeVariant(level) {
  const l = String(level || '').toLowerCase()
  if (l === 'high') return 'light-success'
  if (l === 'medium') return 'light-warning'
  if (l === 'low' || l === 'none' || l === 'not_interested') return 'light-danger'
  return 'light-secondary'
}

/**
 * @param {string|null|undefined} category
 * @returns {'success'|'warning'|'danger'|'info'|'secondary'}
 */
export function outcomeBadgeVariant(category) {
  const c = String(category || '').toLowerCase()
  if (c === 'interested' || c === 'positive') return 'light-success'
  if (c === 'undecided' || c === 'neutral') return 'light-warning'
  if (c === 'not_interested' || c === 'negative') return 'light-danger'
  return 'light-secondary'
}

/**
 * @param {string} key
 * @returns {string}
 */
export function hygieneCheckLabel(key) {
  return HYGIENE_CHECK_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase())
}

/**
 * @param {object|null|undefined} check
 * @returns {{ done: boolean|null, confidence: number|null, notes: string|null, extras: object }}
 */
export function formatHygieneCheck(check) {
  if (!check || typeof check !== 'object') {
    return { done: null, confidence: null, notes: null, extras: {} }
  }
  const done = typeof check.done === 'boolean' ? check.done : null
  const confidence = check.confidence != null ? Number(check.confidence) : null
  const notes = check.notes || check.evidence || null
  const extras = { ...check }
  delete extras.done
  delete extras.confidence
  delete extras.notes
  delete extras.evidence
  return { done, confidence, notes, extras }
}

/**
 * @param {object|null|undefined} analysis
 * @returns {number|null}
 */
export function getHygieneScore(analysis) {
  const score = analysis?.hygiene_checks?.overall_hygiene_score
    ?? analysis?.hygiene_score
    ?? analysis?.overall_hygiene_score
  return score != null && !Number.isNaN(Number(score)) ? Number(score) : null
}

/**
 * @param {number|null|undefined} score
 * @param {number} [digits=0]
 * @returns {string}
 */
export function formatScorePercent(score, digits = 0) {
  if (score == null || Number.isNaN(Number(score))) return '—'
  return `${Math.round(Number(score) * 100 * (10 ** digits)) / (10 ** digits)}%`
}

/**
 * @param {object|null|undefined} analysis
 * @returns {Array<{ key: string, label: string, done: boolean|null, confidence: number|null, notes: string|null, extras: object }>}
 */
export function listHygieneChecks(analysis) {
  const checks = analysis?.hygiene_checks
  if (!checks || typeof checks !== 'object') return []
  return Object.keys(HYGIENE_CHECK_LABELS)
    .filter(key => checks[key] != null)
    .map(key => {
      const formatted = formatHygieneCheck(checks[key])
      return {
        key,
        label: hygieneCheckLabel(key),
        ...formatted,
      }
    })
}

/**
 * @param {object|null|undefined} analysis
 * @returns {string}
 */
export function formatOutcomeLabel(analysis) {
  const category = analysis?.call_outcome?.category
    ?? analysis?.outcome_category
    ?? (typeof analysis?.outcome === 'string' ? analysis.outcome : analysis?.outcome?.category)
  if (!category) return '—'
  return String(category).replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase())
}

/**
 * @param {object|null|undefined} analysis
 * @returns {string}
 */
export function formatInterestLabel(analysis) {
  const level = analysis?.candidate_interest?.level
    ?? analysis?.interest_level
    ?? (typeof analysis?.interest === 'string' ? analysis.interest : analysis?.interest?.level)
  if (!level) return '—'
  return String(level).replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase())
}

/**
 * @param {string|null|undefined} level
 * @returns {'success'|'warning'|'danger'|'secondary'|'info'}
 */
export function engagementBadgeVariant(level) {
  const l = String(level || '').toLowerCase()
  if (l === 'high') return 'light-success'
  if (l === 'low') return 'light-warning'
  return 'light-secondary'
}

/**
 * @param {string|null|undefined} level
 * @returns {'success'|'warning'|'danger'|'secondary'|'info'}
 */
export function professionalismBadgeVariant(level) {
  const l = String(level || '').toLowerCase()
  if (l === 'excellent' || l === 'good') return 'light-primary'
  return 'light-secondary'
}

/**
 * @param {object|null|undefined} analysis
 * @returns {object|null}
 */
export function getCallQuality(analysis) {
  const quality = analysis?.call_quality
  if (quality && typeof quality === 'object') return quality
  if (analysis?.quality && typeof analysis.quality === 'object') return analysis.quality
  return null
}

/**
 * Human-readable label for a red-flag item (string or object).
 * @param {*} flag
 * @returns {string}
 */
export function normalizeFlagLabel(flag) {
  if (flag == null || flag === false) return ''
  if (typeof flag === 'string') return flag.trim()
  if (typeof flag === 'number') return String(flag)
  if (typeof flag === 'object') {
    const label = flag.label || flag.message || flag.text || flag.reason
      || flag.description || flag.type || flag.flag
    if (typeof label === 'string') return label.trim()
    if (typeof label === 'number') return String(label)
  }
  return ''
}

/**
 * @param {object|null|undefined} analysis
 * @returns {string[]}
 */
export function getRedFlags(analysis) {
  const flags = getCallQuality(analysis)?.red_flags ?? analysis?.red_flags
  if (!Array.isArray(flags)) return []
  return flags.map(normalizeFlagLabel).filter(Boolean)
}

/**
 * Flag count for History quality badges. Uses labels when present, else red_flag_count.
 * @param {object|null|undefined} analysis
 * @returns {number}
 */
export function getRedFlagCount(analysis) {
  const flags = getRedFlags(analysis)
  if (flags.length) return flags.length
  const n = Number(analysis?.red_flag_count ?? getCallQuality(analysis)?.red_flag_count)
  return Number.isFinite(n) && n > 0 ? n : 0
}

/** @typedef {{ key: string, type: 'red_flag'|'hygiene'|'status_mismatch', label: string }} CallFlag */

export const STATUS_MISMATCH_FLAG_LABEL = 'Interest / status mismatch'

/**
 * All hygiene / quality / status flags on one analyzed call.
 * @param {object|null|undefined} row
 * @returns {CallFlag[]}
 */
export function listCallFlags(row) {
  const analysis = parseAnalysis(row)
  const flags = []

  getRedFlags(analysis).forEach(label => {
    flags.push({
      key: `red_flag:${label.toLowerCase()}`,
      type: 'red_flag',
      label,
    })
  })

  getMissedHygieneChecks(analysis).forEach(check => {
    flags.push({
      key: `hygiene:${check.key}`,
      type: 'hygiene',
      label: check.label,
    })
  })

  if (hasInterestStatusMismatch(row)) {
    flags.push({
      key: 'status_mismatch',
      type: 'status_mismatch',
      label: STATUS_MISMATCH_FLAG_LABEL,
    })
  }

  return flags
}

/**
 * @param {string} type
 * @returns {string}
 */
export function callFlagTypeLabel(type) {
  if (type === 'hygiene') return 'Missed hygiene'
  if (type === 'status_mismatch') return 'Status'
  return 'Quality'
}

/**
 * Group flags across a recruiter's period calls, with example call rows.
 * Each example keeps the live period call so the flags modal can open
 * analysis and resolve a recording (sid, callDbId, recordingS3Key, analysis).
 * @param {Array<object>} calls
 * @param {number} [maxExamples=12]
 * @returns {{ total: number, groups: Array<{ key: string, type: string, label: string, count: number, examples: Array<{ candidate: string, date: string, ts: number, call: object }> }> }}
 */
export function groupCallFlags(calls, maxExamples = 12) {
  const map = {}
  if (!Array.isArray(calls) || !calls.length) return { total: 0, groups: [] }

  calls.forEach(call => {
    const candidate = firstNonEmptyString(
      call.candidate && call.candidate !== '—' ? call.candidate : '',
      call.phone && call.phone !== '—' ? call.phone : '',
    ) || '—'
    const example = {
      candidate,
      date: call.date || '—',
      ts: Number(call._ts) || 0,
      call,
    }
    listCallFlags(call).forEach(flag => {
      if (!map[flag.key]) {
        map[flag.key] = {
          key: flag.key,
          type: flag.type,
          label: flag.label,
          count: 0,
          examples: [],
        }
      }
      const group = map[flag.key]
      group.count += 1
      group.examples.push(example)
    })
  })

  const groups = Object.values(map)
    .map(group => {
      const examples = group.examples
        .sort((a, b) => b.ts - a.ts)
        .slice(0, Math.max(1, maxExamples))
      return { ...group, examples }
    })
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))

  return {
    total: groups.reduce((sum, group) => sum + group.count, 0),
    groups,
  }
}

/**
 * @param {object|null|undefined} analysis
 * @returns {Array<{ key: string, label: string, done: boolean|null, confidence: number|null, notes: string|null, extras: object }>}
 */
export function getMissedHygieneChecks(analysis) {
  const fromFull = listHygieneChecks(analysis).filter(check => check.done === false)
  if (fromFull.length) return fromFull
  const keys = analysis?.missed_hygiene || analysis?.missed_hygiene_checks
  if (!Array.isArray(keys) || !keys.length) return []
  return keys.filter(Boolean).map(key => {
    const id = String(key)
    return {
      key: id,
      label: hygieneCheckLabel(id),
      done: false,
      confidence: null,
      notes: null,
      extras: {},
    }
  })
}

/**
 * @param {Array<object>} calls
 * @returns {number|null}
 */
export function averageHygieneScore(calls) {
  if (!Array.isArray(calls) || !calls.length) return null
  const scores = calls
    .map(call => getHygieneScore(parseAnalysis(call)))
    .filter(score => score != null && !Number.isNaN(Number(score)))
  if (!scores.length) return null
  return scores.reduce((sum, score) => sum + Number(score), 0) / scores.length
}

/**
 * @param {string|null|undefined} level
 * @returns {string}
 */
export function formatQualityLevel(level) {
  if (!level) return '—'
  return String(level).replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase())
}

/**
 * @typedef {{ label: string, variant: string }} CallTypeTag
 */

/**
 * @param {object|null|undefined} row
 * @returns {boolean}
 */
function isCandidateLinkedToJob(row) {
  if (!row) return false
  const candidateId = row.candidateId ?? row.candidate_id
  const jobId = row.jobId ?? row.job_id
  return candidateId != null && candidateId !== '' && jobId != null && jobId !== ''
}

/**
 * Derive a call-nature badge (Follow-up or Fresh Call) for a mapped call row.
 * Based on recruiter-updated candidateStatus, not AI analysis.
 * Shown for answered calls when status/job linkage supports classification.
 * @param {object|null|undefined} row
 * @returns {CallTypeTag|null}
 */
export function getCallTypeTag(row) {
  if (!isAnsweredCall(row)) return null

  if (isProfileSharedOrBeyond(getCandidateStatusFromRow(row))) {
    return { label: 'Follow-up', variant: 'light-info' }
  }
  if (isCandidateLinkedToJob(row)) {
    return { label: 'Fresh Call', variant: 'light-primary' }
  }
  return null
}

/**
 * Normalize Exotel/API direction values (e.g. inbound, inbound-api, outbound-api).
 * @param {string|null|undefined} raw
 * @returns {'Inbound'|'Outbound'}
 */
export function normalizeDirection(raw) {
  if (!raw) return 'Outbound'
  const lower = String(raw).toLowerCase()
  if (lower.includes('inbound') || lower === 'incoming') return 'Inbound'
  return 'Outbound'
}

/**
 * @param {string|null|undefined} raw
 * @returns {boolean}
 */
export function isInboundDirection(raw) {
  if (!raw) return false
  const lower = String(raw).toLowerCase()
  return lower.includes('inbound') || lower === 'incoming'
}

/**
 * Resolve display direction for a mapped call row.
 * @param {object|null|undefined} row
 * @returns {'Inbound'|'Outbound'}
 */
export function getDisplayDirection(row) {
  if (!row) return 'Outbound'
  if (row.direction === 'Inbound' || row.direction === 'Outbound') return row.direction
  return normalizeDirection(row._rawDirection || row.direction || '')
}

/**
 * Bootstrap badge variant for call direction pills.
 * @param {'Inbound'|'Outbound'|string|null|undefined} direction
 * @returns {string}
 */
export function directionBadgeVariant(direction) {
  const d = String(direction || '').toLowerCase()
  if (d === 'inbound') return 'light-info'
  if (d === 'outbound') return 'light-success'
  return 'light-secondary'
}

/**
 * Bootstrap badge variant for red-flag count pills.
 * @param {number} flagCount
 * @returns {string}
 */
export function flagsBadgeVariant(flagCount) {
  return flagCount > 0 ? 'light-warning' : 'light-secondary'
}

/**
 * Classify call nature for recruiter summary counts.
 * Follow-up when profile shared or beyond; fresh call when linked to job but earlier stage.
 * Applies to answered calls only; does not require completed AI analysis.
 * @param {object|null|undefined} row
 * @returns {'follow-up'|'fresh-call'|null}
 */
export function getCallNature(row) {
  if (!isAnsweredCall(row)) return null

  if (isProfileSharedOrBeyond(getCandidateStatusFromRow(row))) return 'follow-up'
  if (isCandidateLinkedToJob(row)) return 'fresh-call'
  return null
}

/**
 * Whether a recruiter name is a generic placeholder (e.g. "Recruiter SH-091").
 * @param {string|null|undefined} name
 * @returns {boolean}
 */
export function isGenericRecruiterName(name) {
  if (!name) return false
  return /^Recruiter\s+SH-\d+/i.test(String(name).trim())
}

/**
 * Resolve the best display name for a recruiter.
 * Prefers recruiterNameMap and real names over generic "Recruiter SH-XXX" placeholders.
 * @param {string|null|undefined} empId
 * @param {Record<string, string>} [recruiterNameMap]
 * @param {string|null|undefined} [fallbackName]
 * @returns {string}
 */
export function resolveRecruiterDisplayName(empId, recruiterNameMap = {}, fallbackName = '') {
  const id = String(empId || '').trim()
  const mapName = recruiterNameMap[id]
  const fallback = String(fallbackName || '').trim()

  if (mapName && mapName !== id && !isGenericRecruiterName(mapName)) return mapName
  if (fallback && !isGenericRecruiterName(fallback)) return fallback
  if (mapName) return mapName
  if (fallback) return fallback
  return id || '—'
}

/**
 * @typedef {object} RecruiterAnalysisSummary
 * @property {string} empId
 * @property {string} name
 * @property {number} total
 * @property {number} freshCall
 * @property {number} followUp
 * @property {number|null} avgHygiene
 * @property {number} statusMismatch
 * @property {number} flagCount
 */

/**
 * Group analyzed calls by recruiter for team/org summary views.
 * @param {Array<object>} calls
 * @param {Record<string, string>} [recruiterNameMap]
 * @returns {RecruiterAnalysisSummary[]}
 */
export function groupAnalysisByRecruiter(calls, recruiterNameMap = {}) {
  if (!Array.isArray(calls) || !calls.length) return []

  const map = {}
  calls.forEach(call => {
    const empId = call._rawRecruiter || ''
    if (!empId) return

    if (!map[empId]) {
      map[empId] = {
        empId,
        name: resolveRecruiterDisplayName(empId, recruiterNameMap, call._recruiterName),
        total: 0,
        freshCall: 0,
        followUp: 0,
        statusMismatch: 0,
        flagCount: 0,
        hygieneSum: 0,
        hygieneCount: 0,
      }
    } else {
      const betterName = resolveRecruiterDisplayName(empId, recruiterNameMap, call._recruiterName)
      if (isGenericRecruiterName(map[empId].name) && !isGenericRecruiterName(betterName)) {
        map[empId].name = betterName
      }
    }

    const entry = map[empId]
    entry.total += 1

    const nature = getCallNature(call)
    if (nature === 'follow-up') entry.followUp += 1
    else if (nature === 'fresh-call') entry.freshCall += 1
    if (hasInterestStatusMismatch(call)) entry.statusMismatch += 1
    entry.flagCount += listCallFlags(call).length

    const score = getHygieneScore(parseAnalysis(call))
    if (score != null && !Number.isNaN(Number(score))) {
      entry.hygieneSum += Number(score)
      entry.hygieneCount += 1
    }
  })

  return Object.values(map)
    .map(entry => ({
      empId: entry.empId,
      name: entry.name,
      total: entry.total,
      freshCall: entry.freshCall,
      followUp: entry.followUp,
      avgHygiene: entry.hygieneCount > 0 ? entry.hygieneSum / entry.hygieneCount : null,
      statusMismatch: entry.statusMismatch,
      flagCount: entry.flagCount,
    }))
    .sort((a, b) => b.total - a.total)
}
